import { type ParentRollupQuery, reportingKeys } from '@taler/api-client'
import type { Locale } from '@taler/domain'
import { useQuery } from '@tanstack/vue-query'
import { computed, type ComputedRef } from 'vue'
import { useApi } from '@/app/api'
import { retrying } from '@/app/retrying'
import { buildPeriodRollupTable, type PeriodRollupTableData } from '../internal/period-rollup-table.ts'
import { useRollupAccounts } from './use-rollup-accounts.ts'

export interface UsePeriodRollupOptions {
  readonly ledgerId: () => string | undefined
  readonly query: () => ParentRollupQuery
  readonly expanded: () => ReadonlySet<string>
  readonly locale: () => Locale
  readonly enabled: () => boolean
}

export interface UsePeriodRollup {
  readonly table: ComputedRef<PeriodRollupTableData | undefined>
  readonly isLoading: ComputedRef<boolean>
  readonly error: ComputedRef<Error | null>
  readonly retrying: ComputedRef<boolean>
  retry(): void
}

/**
 * One roll-up call carries everything the table needs: the hierarchy, each account's own
 * and rolled-up figure per period, and - since this chart has exactly one root account per
 * class - that root's own rolled-up balance already *is* the period's grand total
 * (`buildPeriodRollupTable`). No second call, no client-side summing.
 */
export function usePeriodRollup(options: UsePeriodRollupOptions): UsePeriodRollup {
  const api = useApi()
  const active = computed(() => options.enabled() && options.ledgerId() !== undefined)

  const rollupQuery = useQuery({
    queryKey: computed(() => [
      ...reportingKeys.parentRollup(options.ledgerId() ?? '', options.query()),
      options.query().class,
    ]),
    queryFn: ({ signal }) => api.reporting.parentRollup(options.ledgerId()!, options.query(), signal),
    enabled: active,
    retry: false,
  })

  const names = useRollupAccounts({
    ledgerId: options.ledgerId,
    accountClass: () => options.query().class,
    enabled: options.enabled,
  })

  const isLoading = computed(() => active.value && (rollupQuery.isPending.value || names.isLoading.value))
  const error = computed(() => rollupQuery.error.value ?? names.error.value)
  const rollupRetrying = retrying(rollupQuery)

  const table = computed(() => {
    if (rollupQuery.data.value === undefined || names.accounts.value === undefined) return undefined
    const accountsById = new Map(names.accounts.value.map((account) => [account.id, account]))
    return buildPeriodRollupTable(
      rollupQuery.data.value,
      accountsById,
      options.expanded(),
      options.locale(),
      options.query().displayCurrency,
    )
  })

  return {
    table,
    isLoading,
    error,
    retrying: computed(() => rollupRetrying.value || names.retrying.value),
    retry: () => {
      void rollupQuery.refetch()
      names.retry()
    },
  }
}
